import { useRef } from "react";

import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import Line from "@/components/Charts/Line";
import { ANALYSIS_DATA_TYPE } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import GradientPlugin from "@components/Charts/plugins/GradientPlugin";
import ReactLegendPlugin from "@components/Charts/plugins/ReactLegendPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";
import prepareDatasets from "@components/Charts/utils/prepareDataset";

import { BaseWidget } from "../BaseWidget";
import CheckboxLegend from "./partials/CheckboxLegend";
import {
  PackedWidgetData,
  reactLegendOnClickForSoundWithIndex,
  tooltipWithDateRangeCallback,
  unpackWidgetData,
} from "./utils";
import { WidgetProps } from "./WidgetProps";
type SentimentPerMediumData = PackedWidgetData<ChartDataset<"line", number[]>>;

const SentimentPerMedium: React.FunctionComponent<
  WidgetProps<SentimentPerMediumData>
> = (props) => {
  const { data, filters } = props;
  const chartData = prepareDatasets(
    unpackWidgetData<ChartDataset<"line", number[]>>(data)
  );

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) =>
      data?.some((d) => typeof d == "number")
    )
  );

  const { formatDate } = useI18n();

  const legendRef = useRef<HTMLDivElement>(null);

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      hasData={hasData}
      toolRef={legendRef}
      {...props}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_SENTIMENT_PER_MEDIUM_INSIGHTS}
    >
      {chartData && (
        <Line
          options={{
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  // render nothing here. This results in just showing the date
                  label: () => "",
                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },
              reactLegend: {
                container: legendRef,
                component: CheckboxLegend,
                hidden: [ANALYSIS_DATA_TYPE.INDUSTRY_INDEX],
                onClick: reactLegendOnClickForSoundWithIndex,
              },
            },

            scales: {
              y: {
                min: -100,
                max: 100,
                ticks: {
                  stepSize: 50,
                  maxTicksLimit: 5, // This should be 0, 25, 50, 75, 100
                  precision: 50, // forces integers

                  callback: function (value) {
                    if (value === 100) {
                      return L.MENTIONS_SENTIMENT_POSITIVE;
                    }
                    if (value === 0) {
                      return L.MENTIONS_SENTIMENT_NEUTRAL;
                    }
                    if (value === -100) {
                      return L.MENTIONS_SENTIMENT_NEGATIVE;
                    }
                    return "";
                  },
                  font: {
                    size: 16,
                  },
                },
              },
              // scale for grid lines and actual data. These labels are hidden
              x: {
                type: "time",
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
              },
              // scale to display months labels
              x2: dynamicIntervalLabels(chartData?.meta?.time_period),
            },
          }}
          data={chartData}
          plugins={[ClickHandlerPlugin, ReactLegendPlugin, GradientPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default SentimentPerMedium;
